import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { can as hasPermission } from '@/shared/auth/session'
import { useSession } from '@/shared/auth/session'

/**
 * The Polling Studio's screens, in four groups.
 *
 * ## Why four groups and not seventeen tabs
 *
 * PollSphere renders seventeen tabs in one horizontal strip. At this shell's
 * density that strip wraps on any laptop, and seventeen undifferentiated tabs is
 * a menu rather than an information architecture — a reader looking for
 * "crosstabs" scans all seventeen every time because nothing tells them which
 * half of the strip to look in.
 *
 * Same screens, grouped by WHEN somebody needs them: Field while fieldwork is
 * running, Design before it starts, Analyse after it stops, Publish at the end.
 * The groups are labels rather than routes — clicking a group would be a
 * navigation that answers no question.
 *
 * ## Why the strip is here and not in the shell
 *
 * `AppShell` takes only `{ children }`, and its `h-9` sub-bar is hardcoded chrome
 * with no slot, portal or context. The sidebar is likewise one level deep
 * (`NavChild` has no `children` field). So the group split lives in this layout,
 * which is registered in `router.tsx` as a parent route — the
 * `ProgramAnalyticsLayout` pattern.
 *
 * ## Permission
 *
 * A group whose every screen is denied is not rendered. Individual screens are
 * NOT hidden on a maybe: a control the caller might be allowed to use is offered
 * and the server's refusal is what speaks, because a hidden control cannot be
 * told apart from a broken one.
 */

interface Tab {
  to: string
  label: string
  hint: string
  /** Omitted when `survey:read` is enough — which is most of them. */
  permission?: string
  end?: boolean
}

interface Group {
  label: string
  tabs: Tab[]
}

export const POLLING_GROUPS: Group[] = [
  {
    label: 'Read',
    tabs: [
      { to: '/polling', label: 'The Estimator', end: true,
        hint: 'What the number can bear — the interval, the gap, and the field behind them',
        permission: 'survey:analyse' },
    ],
  },
  {
    label: 'Field',
    tabs: [
      { to: '/polling/overview', label: 'Overview', hint: 'What is in the field right now' },
      { to: '/polling/polls', label: 'Polls', hint: 'Every poll, with its status and scope' },
      { to: '/polling/fieldwork', label: 'Fieldwork', hint: 'Assignments, response rates and quota fill' },
      // Every interview, not only the flagged ones. Its own authority: reading a
      // poll's design and paging through four hundred people's individual answers
      // are different disclosures.
      { to: '/polling/interviews', label: 'Interviews', hint: 'Every interview on a poll, with its answers and audio', permission: 'survey:read-responses' },
      { to: '/polling/quality', label: 'Quality', hint: 'Flagged interviews and back-checks', permission: 'survey:moderate' },
      // The interviewer's half of Fieldwork. `survey:field` rather than
      // `survey:read`, because this is the one tab in the module that somebody
      // who only conducts interviews needs — and the only one they should be
      // sent to first.
      // `survey:read`, like the rest of Field — the only estimate on it is a
      // leading share that is absent whenever the weight run justifying it is.
      // It is not in Analyse, where every tab gates on `survey:analyse`.
      { to: '/polling/map', label: 'Map', hint: 'Where the fieldwork landed, aggregated to units' },
      { to: '/polling/my-work', label: 'My work', hint: 'Your own assignments, and the attempts against them', permission: 'survey:field' },
    ],
  },
  {
    label: 'Design',
    tabs: [
      // FIRST in the Design group: it is where a poll is set up, and the
      // instrument is one step inside it. A reader who lands on Builder with no
      // poll chosen has skipped the screen that creates one.
      //
      // Builder KEEPS its own tab, and it is the same component either way —
      // the configurator mounts it with `embeddedPollId`. Somebody who only
      // wants to add a question should not have to walk six steps to reach one,
      // and a second implementation for that shortcut is what this arrangement
      // avoids.
      { to: '/polling/configure', label: 'Configure', hint: 'Purpose, sample, links, questions and schedule — step by step' },
      { to: '/polling/builder', label: 'Builder', hint: 'The instrument on its own, for an edit that needs no wizard' },
      { to: '/polling/sample', label: 'Sample', hint: 'Strata, quotas and the population frame' },
      { to: '/polling/trackers', label: 'Trackers', hint: 'Groups and their waves' },
    ],
  },
  {
    label: 'Analyse',
    tabs: [
      { to: '/polling/results', label: 'Results', hint: 'Published estimates with their bases', permission: 'survey:analyse' },
      { to: '/polling/crosstabs', label: 'Crosstabs', hint: 'Two questions, with their assumptions', permission: 'survey:analyse' },
      { to: '/polling/segments', label: 'Segments', hint: 'Clustering and components', permission: 'survey:analyse' },
      { to: '/polling/verbatims', label: 'Verbatims', hint: 'The coding frame and inter-coder agreement' },
    ],
  },
  {
    label: 'Publish',
    tabs: [
      { to: '/polling/exports', label: 'Exports', hint: 'Datasets, codebooks and verbatims', permission: 'survey:export' },
      { to: '/polling/external', label: 'External polls', hint: 'What everybody else published' },
    ],
  },
]

export function PollingLayout() {
  // The filter object lives in the query string and moving between screens must
  // carry it, or "this poll, this wave" silently becomes "everything" the moment
  // a reader moves from fieldwork to results — and the two screens then disagree
  // for a reason nothing on the page explains.
  const { search } = useLocation()
  // The authority LIST, not one `useCan` per tab: hooks cannot be called in a
  // loop, and thirteen `useCan` calls at the top of a layout is the shape that
  // breaks the moment somebody adds a fourteenth tab conditionally.
  const authorities = useSession((s) => s.authorities)

  const groups = POLLING_GROUPS
    .map((group) => ({
      ...group,
      tabs: group.tabs.filter((tab) => hasPermission(authorities, tab.permission)),
    }))
    .filter((group) => group.tabs.length > 0)

  return (
    <div className="space-y-4">
      <nav aria-label="Polling Studio" className="axis-table-scroll border-b border-border">
        <ul className="flex items-stretch gap-4 min-w-max">
          {groups.map((group) => (
            <li key={group.label} className="flex items-stretch gap-1">
              <span
                className="self-center pr-1 text-erp-xs uppercase tracking-wide text-muted-foreground/70"
                aria-hidden
              >
                {group.label}
              </span>
              {group.tabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={{ pathname: tab.to, search }}
                  // `end` only on the index tab. Without it "Overview" stays lit
                  // on every child route, which reads as two tabs selected at once.
                  end={tab.end}
                  title={tab.hint}
                  className={({ isActive }) => cn(
                    'inline-block whitespace-nowrap px-3 py-2 text-erp-xs font-medium border-b-2 -mb-px transition-colors',
                    isActive
                      ? 'border-chocolate text-chocolate'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {tab.label}
                </NavLink>
              ))}
            </li>
          ))}
        </ul>
      </nav>
      <Outlet />
    </div>
  )
}
